import createDebug from '../utils/debug';
import validate from './validator';
import { fetchJson } from '../utils/fetch';
import { getAppManifestUrl } from '../utils/url';
import { WrappedError } from '../utils/custom-errors';
import { memoryStorage } from '../utils/namespaced-storage';
import { getCachedManifest, cacheManifest } from './manifest-cache';
import { APP_MANIFEST_CACHE_EXPIRES_IN } from '../constants';

const debug = createDebug('manifest:loader');

async function fetchAndCacheManifest(appName) {
  // fetch manifest
  let manifest;
  try {
    const { json, lastModified } = await fetchJson(getAppManifestUrl(appName));
    manifest = { ...json, lastModified };
    debug('fetched', appName, manifest);
  } catch (error) {
    throw new WrappedError(
      `App manifest of ${appName} couldn't be loaded`,
      error,
    );
  }

  // validate fetched manifest
  if (!validate(manifest)) {
    throw new Error(`App manifest of ${appName} is invalid`);
  }

  // cache manifest
  cacheManifest(appName, manifest, memoryStorage);

  return manifest;
}

export default (async function loadAppManifest(appName) {
  // return cached manifest if available & valid
  const cachedManifest = getCachedManifest(
    appName,
    memoryStorage,
    APP_MANIFEST_CACHE_EXPIRES_IN,
  );
  if (cachedManifest && validate(cachedManifest)) {
    // try to update manifest cache for later calls (async)
    fetchAndCacheManifest(appName).catch(err => {
      debug('error updating manifest cache', err);
    });

    return cachedManifest;
  }

  // fetch and cache manifest
  const manifest = await fetchAndCacheManifest(appName);
  return manifest;
});
